import { useState, type FormEvent } from "react";
import { ConfirmButton } from "../components/confirm-button.js";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useAuth } from "../lib/auth-context.js";
import { ApiError } from "../lib/api.js";
import {
  listInvitations,
  listMemberships,
  updateHousehold,
} from "../lib/api-modules/households.js";
import {
  createInvitation,
  revokeInvitation,
} from "../lib/api-modules/invitations.js";
import type {
  HouseholdInvitation,
  HouseholdMember,
  MembershipRole,
} from "../lib/api-modules/types.js";
import { ErrorMessage } from "../components/auth/error-message.js";
import { SuggestedPromptsTile } from "../components/SuggestedPromptsTile.js";
import { PAGE_PROMPTS } from "../lib/data/page-prompts.js";

const ELEVATED: ReadonlySet<MembershipRole> = new Set(["owner", "admin"]);

const INVITABLE_ROLES: MembershipRole[] = [
  "admin",
  "member",
  "contributor",
  "viewer",
];

/**
 * /settings/household — household members + invitations management.
 *
 * Sections:
 *   1. Members table (every active member can read).
 *   2. Invite form (admin-only).
 *   3. Pending invitations table with revoke action (admin-only).
 *
 * Admin gate: viewer/contributor/member roles see the members table
 * but not the invite/revoke surfaces. Owner/admin see everything.
 *
 * household_id comes from auth context (`primary_household_id`). Phase 1
 * is single-household-per-user; Phase 15 will add a switcher.
 */
export function HouseholdSettingsPage() {
  const { user } = useAuth();
  if (!user) return null;
  const householdId = user.primary_household_id;
  const isAdmin = ELEVATED.has(user.role);

  return (
    <div className="mx-auto max-w-4xl p-6">
      <header className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Household settings
          </h1>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
            Manage who has access to your household.
          </p>
        </div>
      </header>

      {isAdmin && <ProfileSection householdId={householdId} />}

      <MembersSection householdId={householdId} />

      {isAdmin ? (
        <>
          <InviteSection householdId={householdId} />
          <PendingInvitationsSection householdId={householdId} />
        </>
      ) : (
        <p className="mt-8 rounded-md border border-zinc-200 bg-zinc-50 p-3 text-sm text-zinc-600 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400">
          You don't have permission to invite or revoke members. Ask an
          owner or admin.
        </p>
      )}
      <div className="mt-8">
        <SuggestedPromptsTile prompts={PAGE_PROMPTS.household} />
      </div>
    </div>
  );
}

// === Profile (Phase 13) ================================================

function ProfileSection({ householdId }: { householdId: string }) {
  const [weddingDate, setWeddingDate] = useState("");
  const [toast, setToast] = useState<string | null>(null);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: () =>
      updateHousehold(householdId, {
        wedding_date: weddingDate.length > 0 ? weddingDate : null,
      }),
    onSuccess: () => {
      setToast("Saved");
      setTimeout(() => setToast(null), 3500);
    },
    onError: (err) => {
      setInlineError(
        err instanceof ApiError
          ? `${err.message} (code: ${err.code})`
          : "Couldn't save. Try again.",
      );
    },
  });

  return (
    <section aria-labelledby="profile-heading" className="mt-6">
      <h2 id="profile-heading" className="mb-3 text-lg font-medium">
        Household profile
      </h2>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setInlineError(null);
          mutation.mutate();
        }}
        className="flex flex-col gap-3 rounded-lg border border-zinc-200 p-4 sm:flex-row sm:items-end dark:border-zinc-800"
      >
        <div className="flex-1">
          <label
            htmlFor="wedding-date"
            className="mb-1 block text-sm font-medium"
          >
            Wedding / anniversary date{" "}
            <span className="text-zinc-500">(optional)</span>
          </label>
          <input
            id="wedding-date"
            type="date"
            value={weddingDate}
            onChange={(e) => setWeddingDate(e.target.value)}
            className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900"
            disabled={mutation.isPending}
          />
          <p className="mt-1 text-xs text-zinc-500">
            Hearth will celebrate this date in your activity feed each year.
          </p>
        </div>
        <button
          type="submit"
          disabled={mutation.isPending}
          className="rounded-md bg-brand-500 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {mutation.isPending ? "Saving…" : "Save profile"}
        </button>
      </form>
      {inlineError && (
        <div className="mt-3">
          <ErrorMessage>{inlineError}</ErrorMessage>
        </div>
      )}
      {toast && (
        <p
          role="status"
          aria-live="polite"
          className="mt-3 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
        >
          {toast}
        </p>
      )}
    </section>
  );
}

// === Members ===========================================================

function MembersSection({ householdId }: { householdId: string }) {
  const { data, isPending, isError, error } = useQuery({
    queryKey: ["memberships", householdId],
    queryFn: () => listMemberships(householdId),
  });

  return (
    <section aria-labelledby="members-heading" className="mt-6">
      <h2 id="members-heading" className="mb-3 text-lg font-medium">
        Members
      </h2>
      {isPending && <p className="text-sm text-zinc-500">Loading…</p>}
      {isError && (
        <ErrorMessage>
          Couldn't load members: {error instanceof Error ? error.message : "unknown"}
        </ErrorMessage>
      )}
      {data && (
        <div className="overflow-hidden rounded-lg border border-zinc-200 dark:border-zinc-800">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-left dark:bg-zinc-900">
              <tr>
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium">Email</th>
                <th className="px-3 py-2 font-medium">Role</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Joined</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800">
              {[...data.memberships]
                .sort((a, b) =>
                  (a.accepted_at ?? a.invited_at).localeCompare(
                    b.accepted_at ?? b.invited_at,
                  ),
                )
                .map((m) => (
                  <MemberRow key={m.user_id} m={m} />
                ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function MemberRow({ m }: { m: HouseholdMember }) {
  const joined = m.accepted_at ?? m.invited_at;
  return (
    <tr>
      <td className="px-3 py-2">{m.display_name ?? "—"}</td>
      <td className="px-3 py-2 text-zinc-600 dark:text-zinc-400">
        {m.email ?? "—"}
      </td>
      <td className="px-3 py-2 capitalize">{m.role}</td>
      <td className="px-3 py-2 capitalize">{m.status}</td>
      <td className="px-3 py-2 text-zinc-600 dark:text-zinc-400">
        {new Date(joined).toLocaleDateString()}
      </td>
    </tr>
  );
}

// === Invite ============================================================

function InviteSection({ householdId }: { householdId: string }) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<MembershipRole>("admin");
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => createInvitation({ email, household_id: householdId, role }),
    onSuccess: async () => {
      setEmail("");
      setRole("admin");
      setToast(`Invitation sent to ${email}`);
      setTimeout(() => setToast(null), 5000);
      await queryClient.invalidateQueries({
        queryKey: ["invitations", householdId],
      });
    },
    onError: (err) => {
      setInlineError(
        err instanceof ApiError
          ? err.code === "DUPLICATE_RESOURCE"
            ? "That person is already a member of this household."
            : err.code === "ROLE_DENIED"
            ? "You don't have permission to invite."
            : `${err.message} (code: ${err.code})`
          : "Couldn't send invitation. Please try again.",
      );
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setInlineError(null);
    mutation.mutate();
  }

  return (
    <section aria-labelledby="invite-heading" className="mt-10">
      <h2 id="invite-heading" className="mb-3 text-lg font-medium">
        Invite a member
      </h2>
      <form
        onSubmit={onSubmit}
        className="flex flex-col gap-3 rounded-lg border border-zinc-200 p-4 sm:flex-row sm:items-end dark:border-zinc-800"
      >
        <div className="flex-1">
          <label
            htmlFor="invite-email"
            className="mb-1 block text-sm font-medium"
          >
            Email
          </label>
          <input
            id="invite-email"
            type="email"
            required
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900"
            disabled={mutation.isPending}
          />
        </div>
        <div className="sm:w-40">
          <label
            htmlFor="invite-role"
            className="mb-1 block text-sm font-medium"
          >
            Role
          </label>
          <select
            id="invite-role"
            value={role}
            onChange={(e) => setRole(e.target.value as MembershipRole)}
            className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900"
            disabled={mutation.isPending}
          >
            {INVITABLE_ROLES.map((r) => (
              <option key={r} value={r}>
                {r.charAt(0).toUpperCase() + r.slice(1)}
              </option>
            ))}
          </select>
        </div>
        <button
          type="submit"
          disabled={mutation.isPending || email.length === 0}
          className="rounded-md bg-brand-500 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {mutation.isPending ? "Sending…" : "Send invitation"}
        </button>
      </form>
      {inlineError && (
        <div className="mt-3">
          <ErrorMessage>{inlineError}</ErrorMessage>
        </div>
      )}
      {toast && (
        <p
          role="status"
          aria-live="polite"
          className="mt-3 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
        >
          {toast}
        </p>
      )}
    </section>
  );
}

// === Pending invitations ==============================================

function PendingInvitationsSection({ householdId }: { householdId: string }) {
  const queryClient = useQueryClient();
  const { data, isPending, isError, error } = useQuery({
    queryKey: ["invitations", householdId],
    queryFn: () => listInvitations(householdId, "pending"),
  });

  const revokeMut = useMutation({
    mutationFn: (invitationId: string) => revokeInvitation(invitationId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["invitations", householdId],
      });
    },
  });

  return (
    <section aria-labelledby="pending-heading" className="mt-10">
      <h2 id="pending-heading" className="mb-3 text-lg font-medium">
        Pending invitations
      </h2>
      {isPending && <p className="text-sm text-zinc-500">Loading…</p>}
      {isError && (
        <ErrorMessage>
          Couldn't load invitations: {error instanceof Error ? error.message : "unknown"}
        </ErrorMessage>
      )}
      {data && data.invitations.length === 0 && (
        <p className="text-sm text-zinc-500">No pending invitations.</p>
      )}
      {data && data.invitations.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-zinc-200 dark:border-zinc-800">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-left dark:bg-zinc-900">
              <tr>
                <th className="px-3 py-2 font-medium">Email</th>
                <th className="px-3 py-2 font-medium">Role</th>
                <th className="px-3 py-2 font-medium">Sent</th>
                <th className="px-3 py-2 font-medium">Expires</th>
                <th className="px-3 py-2 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800">
              {data.invitations.map((inv) => (
                <PendingInvitationRow
                  key={inv.invitation_id}
                  inv={inv}
                  onRevoke={() => revokeMut.mutate(inv.invitation_id)}
                  revoking={revokeMut.isPending && revokeMut.variables === inv.invitation_id}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {revokeMut.isError && (
        <div className="mt-3">
          <ErrorMessage>
            Couldn't revoke:{" "}
            {revokeMut.error instanceof Error
              ? revokeMut.error.message
              : "unknown"}
          </ErrorMessage>
        </div>
      )}
    </section>
  );
}

function PendingInvitationRow({
  inv,
  onRevoke,
  revoking,
}: {
  inv: HouseholdInvitation;
  onRevoke: () => void;
  revoking: boolean;
}) {
  return (
    <tr>
      <td className="px-3 py-2">{inv.email}</td>
      <td className="px-3 py-2 capitalize">{inv.role}</td>
      <td className="px-3 py-2 text-zinc-600 dark:text-zinc-400">
        {new Date(inv.invited_at).toLocaleDateString()}
      </td>
      <td className="px-3 py-2 text-zinc-600 dark:text-zinc-400">
        {new Date(inv.expires_at).toLocaleDateString()}
      </td>
      <td className="px-3 py-2 text-right">
        <ConfirmButton
          label="Revoke"
          ariaLabel={`Revoke invitation for ${inv.email}`}
          onConfirm={onRevoke}
          isPending={revoking}
          pendingLabel="Revoking…"
          size="xs"
        />
      </td>
    </tr>
  );
}
